import { useEffect, useRef, useState } from 'react';
import { Palette, Check } from 'lucide-react';
import {
  getBase, getAccent, setBase, setAccent,
  BASE_LABEL, ACCENT_LABEL,
  type ThemeBase, type ThemeAccent,
} from '../lib/theme';
import { useT } from '../lib/i18n';

const BASES: ThemeBase[] = ['studeny', 'teply'];
const ACCENTS: ThemeAccent[] = ['neutral', 'terakota', 'petrolejova', 'moduly'];

// Volba barevného motivu — ukládá se do prohlížeče, takže si ji každý řídí sám.
export default function ThemePicker() {
  const [open, setOpen] = useState(false);
  const [base, setBaseState] = useState<ThemeBase>(getBase);
  const [accent, setAccentState] = useState<ThemeAccent>(getAccent);
  const boxRef = useRef<HTMLDivElement>(null);
  const t = useT();

  // Zavři při kliknutí mimo a na Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pickBase = (b: ThemeBase) => { setBase(b); setBaseState(b); };
  const pickAccent = (a: ThemeAccent) => { setAccent(a); setAccentState(a); };

  return (
    <div className="relative" ref={boxRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="grid h-9 w-9 place-items-center rounded-md border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300"
        title={t('Barevný motiv')}
        aria-label={t('Barevný motiv')}
        aria-expanded={open}
      >
        <Palette size={16} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-50 w-64 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3 shadow-2xl"
          style={{ animation: 'fadeIn 140ms ease-out' }}
        >
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
            {t('Základ')}
          </div>
          <div className="mb-3 grid grid-cols-2 gap-1.5">
            {BASES.map((b) => (
              <button
                key={b}
                onClick={() => pickBase(b)}
                title={BASE_LABEL[b].hint}
                className={`rounded-lg border px-2 py-1.5 text-left text-xs ${
                  base === b
                    ? 'border-zinc-900 dark:border-zinc-100 font-medium'
                    : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-500'
                }`}
              >
                <span
                  className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full align-middle"
                  style={{ backgroundColor: b === 'teply' ? '#78716c' : '#71717a' }}
                />
                {BASE_LABEL[b].label}
              </button>
            ))}
          </div>

          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
            {t('Akcent')}
          </div>
          <div className="space-y-1">
            {ACCENTS.map((a) => (
              <button
                key={a}
                onClick={() => pickAccent(a)}
                className={`flex w-full items-center gap-2.5 rounded-lg border px-2 py-1.5 text-left ${
                  accent === a
                    ? 'border-zinc-900 dark:border-zinc-100'
                    : 'border-transparent hover:border-zinc-300 dark:hover:border-zinc-600'
                }`}
              >
                <span
                  className="h-4 w-4 shrink-0 rounded-full border border-black/10"
                  style={{ background: ACCENT_LABEL[a].swatch }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium">{ACCENT_LABEL[a].label}</span>
                  <span className="block truncate text-[10px] text-zinc-500">{ACCENT_LABEL[a].hint}</span>
                </span>
                {accent === a && <Check size={13} className="shrink-0 text-zinc-500" />}
              </button>
            ))}
          </div>

          <p className="mt-2.5 border-t border-zinc-100 dark:border-zinc-800 pt-2 text-[10px] leading-snug text-zinc-400">
            {t('Volba platí jen pro tebe a toto zařízení. Nabídky v PDF zůstávají beze změny.')}
          </p>
        </div>
      )}
    </div>
  );
}
